import { AxiosError, isAxiosError } from 'axios';
import { format } from 'date-fns';
import { atom } from 'jotai';
import { useCallback, useMemo, useRef, useState } from 'react';
import { AccountResource, HistoricalBalanceRecord } from '../../resources';
import { Store } from '../../state';
import { SelectedAccountOrClient } from '../../types';
import { Loadable, LoadableState, SERVER_DATE_FORMAT } from '../../utils';
import { ChartRangeItem } from '../useChartRanges';
import { GetResourceHeaderForMultiAccount, GetResourceHeaderWithToken } from '../useResource';

export type UseHistoricalBalanceCommonParams = {
  accountResource: AccountResource;
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  getResourceHeaderForMultiAccount: GetResourceHeaderForMultiAccount;
  store: Store;
};

const accountIdsByClientAtom = atom<Record<string, string[]>>({});

export const useHistoricalBalanceCommon = ({
  accountResource,
  getResourceHeaderWithToken,
  getResourceHeaderForMultiAccount,
  store,
}: UseHistoricalBalanceCommonParams) => {
  const [historicalBalances, setHistoricalBalances] = useState<LoadableState<HistoricalBalanceRecord>>(
    Loadable.uninitialized,
  );
  const historicalBalancesAbortController = useRef<AbortController | null>(null);

  const getHistoricalBalancesByAccount = useCallback(
    async (startDate: string, endDate: string, abortController?: AbortController) => {
      return (
        await accountResource.getHistoricalBalances(
          {
            accountIdAndAccountCategory: getResourceHeaderForMultiAccount().accountId,
            startDate: startDate,
            endDate: endDate,
          },
          getResourceHeaderWithToken(),
          { signal: abortController?.signal },
        )
      ).data;
    },
    [accountResource, getResourceHeaderWithToken, getResourceHeaderForMultiAccount],
  );

  const getHistoricalBalancesByClient = useCallback(
    async (startDate: string, endDate: string, accountIdList: string[], abortController?: AbortController) => {
      return (
        await accountResource.getHistoricalBalancesByClient(
          {
            accountIdAndAccountCategory: accountIdList,
            startDate: startDate,
            endDate: endDate,
          },
          getResourceHeaderWithToken(),
          { signal: abortController?.signal },
        )
      ).data;
    },
    [accountResource, getResourceHeaderWithToken],
  );

  const buildAccountIdsByClient = (accountIdAndAccountCategory: string[] = []): Record<string, string[]> => {
    return accountIdAndAccountCategory.reduce(
      (acc, account) => {
        const [clientId] = account.split('_');
        acc[clientId] = acc[clientId] ? [...acc[clientId], account] : [account];
        return acc;
      },
      {} as Record<string, string[]>,
    );
  };

  const getHistoricalAccounts = useCallback(
    async (mainClientId: string): Promise<Record<string, string[]>> => {
      const cachedAccountIdsByClient = store.get(accountIdsByClientAtom);

      // later optimization:
      // if cached, return from cache
      // if (cachedAccountIdsByClient[mainClientId]) {
      //   return cachedAccountIdsByClient;
      // }

      const response = await accountResource.getHistoricalAccounts(
        { mainAccountId: mainClientId },
        getResourceHeaderWithToken(),
      );

      const accountIdAndAccountCategory = response.data.accountIdAndAccountCategory ?? [];
      const accountIdsByClient = buildAccountIdsByClient(accountIdAndAccountCategory);
      const mergedAccountIdsByClient = { ...cachedAccountIdsByClient, ...accountIdsByClient };

      if (accountIdAndAccountCategory.length > 0) {
        store.set(accountIdsByClientAtom, mergedAccountIdsByClient);
      }

      return mergedAccountIdsByClient;
    },
    [accountResource, getResourceHeaderWithToken, store],
  );

  // in the futre i hope we can remove clientId param
  const loadHistoricalBalancesByInterval = useCallback(
    async (
      range: ChartRangeItem,
      selectedAccountOrClient: SelectedAccountOrClient,
      mainClientId: string,
    ): Promise<void> => {
      historicalBalancesAbortController.current?.abort();
      const abortController = new AbortController();
      historicalBalancesAbortController.current = abortController;
      const startDate = format(range.start, SERVER_DATE_FORMAT);
      const endDate = format(range.end, SERVER_DATE_FORMAT);

      setHistoricalBalances(curr => (curr.isLoaded ? Loadable.loadingWith(curr) : Loadable.loading));

      try {
        const accountIdsByClient = await getHistoricalAccounts(mainClientId);
        const { account, client } = selectedAccountOrClient;

        if (account) {
          const data = await getHistoricalBalancesByAccount(startDate, endDate, abortController);
          if (historicalBalancesAbortController.current !== abortController) {
            return;
          }
          setHistoricalBalances(Loadable.loaded(data));
          return;
        }

        const clientAccountIds = accountIdsByClient?.[client] ?? [];
        const data = await getHistoricalBalancesByClient(startDate, endDate, clientAccountIds, abortController);
        if (historicalBalancesAbortController.current !== abortController) {
          return;
        }

        setHistoricalBalances(Loadable.loaded(data));
      } catch (error) {
        if (isAxiosError(error) && error.code === AxiosError.ERR_CANCELED) {
          return;
        }
        setHistoricalBalances(Loadable.failed);
      }
    },
    [getHistoricalBalancesByAccount, getHistoricalBalancesByClient, getHistoricalAccounts],
  );

  return useMemo(
    () => ({
      getHistoricalBalancesByAccount,
      historicalBalances,
      loadHistoricalBalancesByInterval,
    }),
    [getHistoricalBalancesByAccount, historicalBalances, loadHistoricalBalancesByInterval],
  );
};
